import { observable, transaction, makeObservable } from "mobx"
import { BaseStore } from "stores/base.store"
import {
  getAllCountryGroups,
  getCountryGroupById,
  createCountryGroup,
  updateCountryGroup,
  deleteCountryGroup,
  addCountriesToGroup,
  removeCountriesFromGroup,
} from "../api/country-groups.call"
import {
  CountryGroupDto,
  CreateCountryGroupDto,
  AddCountriesDto,
  RemoveCountriesDto,
  CountryMultiplierDto,
  CreateCountryMultiplierDto,
} from "./types"
import {
  getAllCountryMultipliers,
  getCountryMultiplierById,
  createCountryMultiplier,
  updateCountryMultiplier,
  deleteCountryMultiplier,
} from "../api/country-multipliers.call"

export class CountryGroupsStore extends BaseStore {
  @observable
  private _countryGroups: CountryGroupDto[] = []

  @observable
  private _selectedGroup: CountryGroupDto | null = null

  @observable
  private _countryMultipliers: CountryMultiplierDto[] = []

  @observable
  private _selectedMultiplier: CountryMultiplierDto | null = null

  @observable
  public isLoading: boolean = false

  constructor(rootStore: any) {
    super(rootStore)
    makeObservable(this)
  }

  public async getAllCountryGroups(): Promise<CountryGroupDto[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getAllCountryGroups)

    if (res) {
      transaction(() => {
        this._countryGroups = res
      })
    }

    return res
  }

  public async getCountryGroupById(id: string): Promise<CountryGroupDto> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getCountryGroupById, id)

    if (res) {
      transaction(() => {
        this._selectedGroup = res
      })
    }

    return res
  }

  public async createCountryGroup(data: CreateCountryGroupDto): Promise<CountryGroupDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryGroups()
    }

    const res = await this.defaultFunction(createCountryGroup, data)

    return res
  }

  public async updateCountryGroup(id: string, data: CreateCountryGroupDto): Promise<CountryGroupDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryGroups()
      if (this._selectedGroup?.id === id) {
        await this.getCountryGroupById(id)
      }
    }

    const res = await this.defaultFunction(updateCountryGroup, id, data)

    return res
  }

  public async deleteCountryGroup(id: string): Promise<void> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryGroups()
      if (this._selectedGroup?.id === id) {
        this._selectedGroup = null
      }
    }

    await this.defaultFunction(deleteCountryGroup, id)
  }

  public async addCountriesToGroup(id: string, data: AddCountriesDto): Promise<CountryGroupDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryGroups()
      if (this._selectedGroup?.id === id) {
        await this.getCountryGroupById(id)
      }
    }

    const res = await this.defaultFunction(addCountriesToGroup, id, data)

    return res
  }

  public async removeCountriesFromGroup(id: string, data: RemoveCountriesDto): Promise<CountryGroupDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryGroups()
      if (this._selectedGroup?.id === id) {
        await this.getCountryGroupById(id)
      }
    }

    const res = await this.defaultFunction(removeCountriesFromGroup, id, data)

    return res
  }

  get countryGroups(): CountryGroupDto[] {
    return this._countryGroups
  }

  get selectedGroup(): CountryGroupDto | null {
    return this._selectedGroup
  }

  public async getAllCountryMultipliers(): Promise<CountryMultiplierDto[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getAllCountryMultipliers)

    if (res) {
      transaction(() => {
        this._countryMultipliers = res
      })
    }

    return res
  }

  public async getCountryMultiplierById(id: string): Promise<CountryMultiplierDto> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getCountryMultiplierById, id)

    if (res) {
      transaction(() => {
        this._selectedMultiplier = res
      })
    }

    return res
  }

  public async createCountryMultiplier(data: CreateCountryMultiplierDto): Promise<CountryMultiplierDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryMultipliers()
    }

    const res = await this.defaultFunction(createCountryMultiplier, data)

    return res
  }

  public async updateCountryMultiplier(id: string, data: CreateCountryMultiplierDto): Promise<CountryMultiplierDto> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryMultipliers()
      if (this._selectedMultiplier?.id === id) {
        await this.getCountryMultiplierById(id)
      }
    }

    const res = await this.defaultFunction(updateCountryMultiplier, id, data)

    return res
  }

  public async deleteCountryMultiplier(id: string): Promise<void> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      await this.getAllCountryMultipliers()
      if (this._selectedMultiplier?.id === id) {
        this._selectedMultiplier = null
      }
    }

    await this.defaultFunction(deleteCountryMultiplier, id)
  }

  get countryMultipliers(): CountryMultiplierDto[] {
    return this._countryMultipliers
  }

  get selectedMultiplier(): CountryMultiplierDto | null {
    return this._selectedMultiplier
  }
}
